import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Homepage from './Pages/Homepage';
import Login from './Pages/Login';
import Logout from './Pages/Logout';
import ChangePassword from './Pages/ChangePassword';
import SetPassword from './Pages/SetPassword';
import ForgotPassword from './Pages/ForgotPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MustChangePasswordGate from './components/auth/MustChangePasswordGate';
import ClientDashboard from './Pages/Portal/ClientDashboard';
import Overview from './Pages/Portal/Overview';
import DashboardTickets from './Pages/Portal/DashboardTickets';
import DashboardEdits from './Pages/Portal/DashboardEdits';
import VisualEditor from './Pages/Portal/VisualEditor';
import VisualEditorComingSoon from './Pages/Portal/VisualEditorComingSoon';
import { VISUAL_EDITOR_ENABLED } from './config/featureFlags';
import PortalInvoices from './Pages/Portal/Invoices';
import PortalInvoiceDetail from './Pages/Portal/InvoiceDetail';
import PortalNotificationSettings from './Pages/Portal/Settings/Notifications';
import PortalMediaCalendar from './Pages/Portal/MediaCalendar';
import AdminLayout from './Pages/Admin/AdminLayout';
import Dashboard from './Pages/Admin/Dashboard';
import Accounts from './Pages/Admin/Accounts';
import AccountDetail from './Pages/Admin/AccountDetail';
import Pipeline from './Pages/Admin/Pipeline';
import Projects from './Pages/Admin/Projects';
import ProjectDetail from './Pages/Admin/ProjectDetail';
import Shoots from './Pages/Admin/Shoots';
import Financial from './Pages/Admin/Financial';
import Invoices from './Pages/Admin/Invoices';
import InvoiceDetail from './Pages/Admin/InvoiceDetail';
import Communications from './Pages/Admin/Communications';
import StaffCalendar from './Pages/Admin/StaffCalendar';
import Assistant from './Pages/Admin/Assistant';
import AssistantAction from './Pages/Admin/AssistantAction';
import OutreachDetail from './Pages/Admin/OutreachDetail';
import OutreachRedirect from './Pages/Admin/OutreachRedirect';
import BroadcastEditor from './Pages/Admin/BroadcastEditor';
import OutreachEnroll from './Pages/Admin/OutreachEnroll';
import Media from './Pages/Admin/Media';
import MediaLayout from './Pages/Media/MediaLayout';
import MediaDashboard from './Pages/Media/MediaDashboard';
import MediaSettings from './Pages/Media/MediaSettings';
import Audits from './Pages/Admin/Audits';
import AuditDetail from './Pages/Admin/AuditDetail';
import Analytics from './Pages/Admin/Analytics';
import Domains from './Pages/Admin/Domains';
import Tasks from './Pages/Admin/Tasks';
import Users from './Pages/Admin/Users';
import AdminTickets from './Pages/Admin/AdminTickets';
import AdminEditRequests from './Pages/Admin/AdminEditRequests';
import Activity from './Pages/Admin/Activity';
import Crons from './Pages/Admin/System/Crons';
import SystemErrors from './Pages/Admin/System/Errors';
import ScrollRestoration from './components/ScrollRestoration';

function DashboardToPortalRedirect() {
  // Preserves the trailing path after /dashboard so deep-links keep landing
  // on the right portal page. e.g. /dashboard/edits/visual → /portal/edits/visual.
  // Sits outside the <Routes> tree so it works without React Router's nested-
  // splat helpers.
  const path = typeof window !== 'undefined' ? window.location.pathname : '/dashboard';
  const target = path.replace(/^\/dashboard/, '/portal');
  return <Navigate to={target + (typeof window !== 'undefined' ? window.location.search : '')} replace />;
}

const App = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollRestoration />
      <AuthProvider>
        <MustChangePasswordGate>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute role="any">
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          <Route
            path="/portal"
            element={
              <ProtectedRoute role="client">
                <ClientDashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/portal/overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="tickets" element={<DashboardTickets />} />
            <Route path="edits" element={<DashboardEdits />} />
            <Route
              path="edits/visual"
              element={VISUAL_EDITOR_ENABLED ? <VisualEditor /> : <VisualEditorComingSoon />}
            />
            <Route path="invoices" element={<PortalInvoices />} />
            <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
            <Route path="media" element={<PortalMediaCalendar />} />
            <Route path="settings/notifications" element={<PortalNotificationSettings />} />
          </Route>

          {/* Muscle-memory redirects: /dashboard/* → /portal/*. Item 3.1 of the plan. */}
          <Route path="/dashboard" element={<Navigate to="/portal" replace />} />
          <Route path="/dashboard/*" element={<DashboardToPortalRedirect />} />

          {/* MP.2 — /media surface for Media-role users (CJ first, fallbacks
              later). Strictly own-only. Cross-shooter views live under
              /admin/media for admin + ops. */}
          <Route
            path="/media"
            element={
              <ProtectedRoute role="media">
                <MediaLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<MediaDashboard />} />
            <Route path="settings" element={<MediaSettings />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute role="staff">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="pipeline" element={<Pipeline />} />
            {/*
              Item 1.2 (Sprint B): /admin/prospects → /admin/accounts?stage_view=prospect.
              Prospects.jsx + the prospects-legacy fallback were deleted as part
              of the merge; the unified Accounts list now hosts the prospect
              stage-view chip natively.
            */}
            <Route path="prospects" element={<Navigate to="/admin/accounts?stage_view=prospect" replace />} />
            <Route path="accounts" element={<Accounts />} />
            <Route path="accounts/:id" element={<AccountDetail />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="shoots" element={<Shoots />} />
            <Route path="financial" element={<Financial />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="communications" element={<Communications />} />
            <Route path="calendar" element={<StaffCalendar />} />
            <Route path="assistant" element={<Assistant />} />
            <Route path="assistant/actions/:id" element={<AssistantAction />} />
            <Route path="communications/broadcasts/:id" element={<BroadcastEditor />} />
            {/* Outreach now lives under Communications → Sequences; old links follow. */}
            <Route path="outreach" element={<OutreachRedirect />} />
            <Route path="outreach/:id" element={<OutreachDetail />} />
            <Route path="outreach/:id/enroll" element={<OutreachEnroll />} />
            <Route path="media" element={<Media />} />
            <Route path="audits" element={<Audits />} />
            <Route path="audits/:id" element={<AuditDetail />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="domains" element={<Domains />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="users" element={<Users />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="edit-requests" element={<AdminEditRequests />} />
            <Route path="activity" element={<Activity />} />
            <Route path="system/crons" element={<Crons />} />
            <Route path="system/errors" element={<SystemErrors />} />
          </Route>

          {/*
            Sprint J: synthesized /admin/preview/:slug route retired in favor
            of real session impersonation. The Eye icon on /admin/accounts
            now POSTs /api/admin/impersonate/:id and redirects to /portal,
            where the admin operates inside an actual client session with
            an "End preview" banner across the top.
          */}
        </Routes>
        </MustChangePasswordGate>
      </AuthProvider>
    </Router>
  );
};

export default App;
